import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { 
  FaHome, 
  FaCalendarAlt, 
  FaMusic, 
  FaUsers, 
  FaSignOutAlt, 
  FaGuitar, 
  FaUserShield, 
  FaChurch, 
  FaUserCircle, 
  FaCalendarCheck, 
  FaCalendarTimes 
} from 'react-icons/fa';

export default function Layout() {
  const { logout, userDetails } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [churchName, setChurchName] = useState('Escala Louvor');
  const [churchLogo, setChurchLogo] = useState(null);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const docSnap = await getDoc(doc(db, "church_settings", "config"));
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.name) setChurchName(data.name);
          if (data.logoUrl) setChurchLogo(data.logoUrl);
        }
      } catch (e) { console.log("Erro config:", e); }
    };
    loadConfig();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  // === DEFINIÇÃO DOS ITENS DO MENU ===
  const itemInicio = { name: 'Início', path: '/', icon: <FaHome /> };
  const itemEscalas = { name: 'Escalas', path: '/escalas', icon: <FaCalendarAlt /> };
  const itemMusicas = { name: 'Músicas', path: '/musicas', icon: <FaMusic /> };
  const itemIndisp = { name: 'Indisponibilidades', path: '/indisponibilidade', icon: <FaCalendarTimes /> }; // Renomeado
  const itemPerfil = { name: 'Perfil', path: '/perfil', icon: <FaUserCircle /> };

  let menuItems = [];

  if (userDetails?.isAdmin) {
    // === MENU ADMIN (8 Itens) ===
    menuItems = [
      itemInicio,
      itemEscalas,
      itemMusicas,
      { name: 'Músicos', path: '/musicos', icon: <FaUsers /> },
      { name: 'Instrum.', path: '/instrumentos', icon: <FaGuitar /> },      
      { name: 'Igreja', path: '/igreja', icon: <FaChurch /> },
      itemPerfil // Adicionado conforme solicitado
    ];
  } else {
    // === MENU MEMBRO (5 Itens) ===
    menuItems = [
      itemInicio,
      itemEscalas,
      itemMusicas,
      itemIndisp, // Adicionado conforme solicitado
      itemPerfil
    ];
  }

  const BrandDisplay = () => (
    <div className="text-center">
      {churchLogo && <img src={churchLogo} alt="Logo" className="h-16 w-auto mx-auto mb-2 object-contain" />}
      <h1 className="text-xl font-bold text-brand-gold leading-tight">{churchName}</h1>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-100">
      {/* === SIDEBAR DESKTOP === */}
      <aside className="hidden md:flex flex-col w-64 bg-brand-blue text-white flex-shrink-0">
        <div className="p-6 border-b border-blue-800">
          <BrandDisplay />
          
          {/* Atalho de Perfil no Topo */}
          <Link to="/perfil" className="block mt-4 text-center border-t border-blue-800 pt-4 hover:bg-blue-800 rounded transition p-2 group">
            <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-2 group-hover:bg-brand-gold group-hover:text-brand-blue transition">
                <FaUserCircle size={24} />
            </div>
            <p className="text-xs font-bold opacity-90 group-hover:opacity-100">
              {userDetails?.musicianName || 'Visitante'}
            </p>
            {userDetails?.isAdmin && <span className="inline-block mt-1 ml-1 bg-brand-gold text-brand-blue px-1.5 py-0.5 rounded font-bold text-[10px]">ADMIN</span>}
          </Link>
        </div>
        
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto custom-scrollbar">
          {menuItems.map((item) => (
            <Link key={item.path} to={item.path} className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${isActive(item.path) ? 'bg-white text-brand-blue font-bold' : 'hover:bg-blue-800 text-gray-100'}`}>
              <span className="text-xl">{item.icon}</span><span>{item.name}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-blue-800">
          <button onClick={handleLogout} className="flex items-center space-x-3 p-3 w-full text-red-200 hover:bg-blue-800 rounded-lg"><FaSignOutAlt /><span>Sair</span></button>
        </div>
      </aside>

      {/* === CONTEÚDO PRINCIPAL === */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        <header className="md:hidden bg-brand-blue text-white p-3 flex justify-between items-center shadow-md z-10 flex-shrink-0">
          <div className="flex items-center gap-2">
             {churchLogo && <img src={churchLogo} className="h-8 w-8 object-contain" alt="logo" />}
             <div><h1 className="text-lg font-bold text-brand-gold leading-none">{churchName}</h1><p className="text-[10px] opacity-80">Olá, {userDetails?.musicianName?.split(' ')[0]}</p></div>
          </div>
          <button onClick={handleLogout} className="text-white opacity-80"><FaSignOutAlt /></button>
        </header>

        <main className="flex-1 overflow-y-auto p-4 pb-24 md:p-8">
          <Outlet />
        </main>

        {/* === BOTTOM NAV MOBILE === */}
        <nav className="md:hidden fixed bottom-0 w-full bg-white border-t border-gray-200 flex overflow-x-auto py-2 z-20 pb-safe scrollbar-hide">
          <div className="flex w-full justify-between min-w-max px-2 gap-2">
            {/* Removido o .slice() para garantir que TODOS os itens apareçam (com scroll lateral se necessário) */}
            {menuItems.map((item) => (
              <Link key={item.path} to={item.path} className={`flex flex-col items-center p-2 min-w-[64px] ${isActive(item.path) ? 'text-brand-blue' : 'text-gray-400'}`}>
                <span className="text-xl mb-1">{item.icon}</span><span className="text-[10px] font-medium truncate max-w-[64px]">{item.name}</span>
              </Link>
            ))}
          </div>
        </nav>
      </div>
    </div>
  );
}